import {
	TCustomer,
	TFilterGroup,
	TFilterCondition,
	TSmartVariableDefinition,
} from '@/types/TSmartCRM';
import { OPERATOR_BACKEND_MAP } from 'src/constants/smartCRM';
import { EFieldType, EFilterLogic, EFilterOperator } from 'src/enums/ESmartCRM';

export const applyFilter = (customers: TCustomer[], condition: TFilterCondition): TCustomer[] => {
	return customers.filter((customer) => {
		const fieldValue = customer[condition.field];
		const filterValue = condition.value;

		switch (condition.operator) {
			case EFilterOperator.EQUALS:
				return fieldValue === filterValue;

			case EFilterOperator.NOT_EQUALS:
				return fieldValue !== filterValue;

			case EFilterOperator.CONTAINS:
				return String(fieldValue).toLowerCase().includes(String(filterValue).toLowerCase());

			case EFilterOperator.NOT_CONTAINS:
				return !String(fieldValue).toLowerCase().includes(String(filterValue).toLowerCase());

			case EFilterOperator.STARTS_WITH:
				return String(fieldValue).toLowerCase().startsWith(String(filterValue).toLowerCase());

			case EFilterOperator.ENDS_WITH:
				return String(fieldValue).toLowerCase().endsWith(String(filterValue).toLowerCase());

			case EFilterOperator.GREATER_THAN:
				return Number(fieldValue) > Number(filterValue);

			case EFilterOperator.LESS_THAN:
				return Number(fieldValue) < Number(filterValue);

			case EFilterOperator.GREATER_EQUAL:
				return Number(fieldValue) >= Number(filterValue);

			case EFilterOperator.LESS_EQUAL:
				return Number(fieldValue) <= Number(filterValue);

			case EFilterOperator.IS_EMPTY:
				return !fieldValue || fieldValue === '' || fieldValue === null;

			case EFilterOperator.IS_NOT_EMPTY:
				return fieldValue && fieldValue !== '' && fieldValue !== null;

			case EFilterOperator.IN:
				if (Array.isArray(filterValue)) {
					return filterValue.includes(fieldValue);
				}
				return false;

			case EFilterOperator.NOT_IN:
				if (Array.isArray(filterValue)) {
					return !filterValue.includes(fieldValue);
				}
				return true;

			default:
				return true;
		}
	});
};

export const applyFilterGroup = (customers: TCustomer[], group: TFilterGroup): TCustomer[] => {
	if (group.conditions.length === 0) {
		return customers;
	}

	return customers.filter((customer) => {
		const results = group.conditions.map((condition) => {
			return applyFilter([customer], condition).length > 0;
		});

		return group.logic === EFilterLogic.AND
			? results.every((result) => result)
			: results.some((result) => result);
	});
};

export const applyFilterGroups = (
	customers: TCustomer[],
	groups: TFilterGroup[],
	globalLogic: EFilterLogic = EFilterLogic.AND,
): TCustomer[] => {
	if (groups.length === 0) {
		return customers;
	}

	const groupResults = groups.map((group) => applyFilterGroup(customers, group));

	if (globalLogic === EFilterLogic.AND) {
		return groupResults.reduce((acc, result) =>
			acc.filter((customer) => result.some((r) => r.id === customer.id)),
		);
	} else {
		const allResults = groupResults.flat();
		return customers.filter((customer) => allResults.some((result) => result.id === customer.id));
	}
};

export const buildFilterSummary = (groups: TFilterGroup[]): string => {
	if (groups.length === 0) {
		return 'No filters applied';
	}

	const groupSummaries = groups.map((group, index) => {
		const conditionSummaries = group.conditions.map((condition) => {
			return `${condition.field} ${condition.operator} ${condition.value}`;
		});

		const logic = group.logic === EFilterLogic.AND ? 'AND' : 'OR';
		return `Group ${index + 1}: ${conditionSummaries.join(` ${logic} `)}`;
	});

	return groupSummaries.join(' AND ');
};

export const validateFilterCondition = (condition: TFilterCondition): boolean => {
	if (!condition.field || !condition.operator) {
		return false;
	}

	const requiresValue = [EFilterOperator.IS_EMPTY, EFilterOperator.IS_NOT_EMPTY];

	if (!requiresValue.includes(condition.operator)) {
		return condition.value !== null && condition.value !== undefined && condition.value !== '';
	}

	return true;
};

export const optimizeFilters = (groups: TFilterGroup[]): TFilterGroup[] => {
	return groups
		.map((group) => ({
			...group,
			conditions: group.conditions.filter((condition) => validateFilterCondition(condition)),
		}))
		.filter((group) => group.conditions.length > 0);
};

export const getFilterableFields = (customers: TCustomer[]): string[] => {
	if (customers.length === 0) {
		return [];
	}

	const allFields = new Set<string>();
	customers.forEach((customer) => {
		Object.keys(customer).forEach((key) => allFields.add(key));
	});

	return Array.from(allFields).filter(
		(field) => field !== 'id' && customers.some((customer) => customer[field] !== null),
	);
};

export const getFieldUniqueValues = (
	customers: TCustomer[],
	fieldKey: string,
): Array<{ value: any; count: number }> => {
	const valueCount = new Map<any, number>();

	customers.forEach((customer) => {
		const value = customer[fieldKey];
		if (value !== null) {
			valueCount.set(value, (valueCount.get(value) || 0) + 1);
		}
	});

	return Array.from(valueCount.entries())
		.map(([value, count]) => ({ value, count }))
		.sort((a, b) => b.count - a.count);
};

export const convertToSnakeCase = (name: string): string => {
	return name
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9\s]/g, '')
		.replace(/\s+/g, '_');
};

export const convertFilterGroupsForBackend = (
	filterGroups: TFilterGroup[],
	fields: TSmartVariableDefinition[],
) => {
	const backendGroups: Array<{
		name: string;
		filters: Array<{
			variableId: string;
			operator: string;
			value: any;
			fieldType: string;
		}>;
	}> = [];

	filterGroups.forEach((group) => {
		const validConditions = group.conditions
			.filter(
				(c) => c.field && c.operator && c.value !== null && c.value !== '' && c.value !== undefined,
			)
			.map((condition) => {
				const field = fields.find((f) => f.internalKey === condition.field);
				let processedValue = condition.value;

				if (field?.fieldType === EFieldType.DATE && condition.value) {
					if (typeof condition.value === 'string') {
						let dateObj;

						if (condition.value.includes('/')) {
							const parts = condition.value.split('/');
							if (parts.length === 3) {
								const month = parseInt(parts[0]);
								const day = parseInt(parts[1]);
								const year = parseInt(parts[2]);
								dateObj = new Date(year, month - 1, day);
							}
						} else if (condition.value.includes('-')) {
							dateObj = new Date(condition.value);
						} else {
							dateObj = new Date(condition.value);
						}

						if (dateObj && !isNaN(dateObj.getTime())) {
							processedValue = dateObj.toISOString();
						} else {
							processedValue = condition.value;
						}
					} else if (condition.value instanceof Date) {
						processedValue = condition.value.toISOString();
					}
				}

				return {
					variableId: field?.id || condition.field,
					operator: OPERATOR_BACKEND_MAP[condition.operator] || condition.operator,
					value: processedValue,
					fieldType: field?.fieldType,
				};
			});

		if (validConditions.length > 0) {
			backendGroups.push({
				name: group.name,
				filters: validConditions,
			});
		}
	});

	return backendGroups;
};

export const convertBackendToFilterGroups = (
	backendData: Record<
		string,
		{
			name: string;
			filters: Array<{
				variableId: string;
				operator: string;
				value: any;
				fieldType: string;
			}>;
		}
	>,
	fields: TSmartVariableDefinition[],
): TFilterGroup[] => {
	const filterGroups: TFilterGroup[] = [];
	const baseTimestamp = Date.now();

	Object.entries(backendData).forEach(([_, groupData], index) => {
		if (!groupData.filters || !Array.isArray(groupData.filters)) {
			return;
		}

		const group: TFilterGroup = {
			id: `group_${baseTimestamp}_${index}`,
			name: groupData.name,
			logic: EFilterLogic.AND,
			conditions: [],
		};

		groupData.filters.forEach((condition, condIndex) => {
			const field = fields.find((f) => f.id === condition.variableId);
			if (field) {
				const frontendOperator = Object.entries(OPERATOR_BACKEND_MAP).find(
					([_, backendOp]) => backendOp === condition.operator,
				)?.[0] as EFilterOperator;

				let processedValue = condition.value;
				if (field.fieldType === EFieldType.DATE && condition.value) {
					if (typeof condition.value === 'string' && condition.value.includes('GMT')) {
						processedValue = new Date(condition.value).toISOString().split('T')[0];
					} else if (typeof condition.value === 'string' && condition.value.includes('T')) {
						processedValue = condition.value.split('T')[0];
					}
				}

				group.conditions.push({
					id: `condition_${baseTimestamp}_${index}_${condIndex}`,
					field: field.internalKey,
					operator: frontendOperator || EFilterOperator.EQUALS,
					value: processedValue,
				});
			}
		});

		if (group.conditions.length > 0) {
			filterGroups.push(group);
		}
	});

	return filterGroups;
};
